import React from "react";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Beverage Machine - Gizlilik Politikası
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          <strong>Son güncelleme:</strong> 14.11.2025
        </p>

        <section className="space-y-6">
          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              1. Genel Bilgi
            </h2>
            <p className="leading-relaxed">
              Bu gizlilik politikası, Beverage Machine mobil uygulamasının
              (bundan sonra “Uygulama”) cihazınızdan hangi verileri hangi
              amaçlarla işlediğini açıklamak için hazırlanmıştır. Uygulama;
              yalnızca içecek makinesinin yönetimi, bağlantı kontrolü ve teknik
              izleme amaçlarıyla çalışır. Uygulama, kullanıcıların kişisel
              verilerini pazarlama veya profil çıkarma amacıyla kullanmaz.
            </p>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              2. Toplanan Veriler, İzinler ve Amaçları
            </h2>
            <p className="leading-relaxed mb-4">
              Uygulama Android işletim sistemi tarafından sağlanan bazı izinleri
              kullanır. Aşağıda, manifest dosyasında yer alan tüm izinler ve
              bunların hangi amaçla kullanıldığı özetlenmiştir.
            </p>

            <SectionWithList
              title="2.1 Ağ ve İnternet İzinleri"
              items={[
                "android.permission.INTERNET",
                "android.permission.ACCESS_NETWORK_STATE",
                "android.permission.ACCESS_WIFI_STATE",
              ]}
              description="Bu izinler; uygulamanın, içecek makinesi ile haberleşmek, sunucuya bağlanmak, bağlantı durumunu kontrol etmek ve olası ağ problemlerini tespit etmek için gereklidir. İnternet trafiği yalnızca sistem fonksiyonlarının gerçekleştirilmesi için kullanılır; üçüncü taraflara pazarlama amacıyla veri aktarımı yapılmaz."
            />

            <SectionWithList
              title="2.2 Telefon Durumu ve Hat Bilgisi"
              items={["android.permission.READ_PHONE_STATE"]}
              description="Bu izin, GSM/4G bağlantısı kullanılan cihazlarda hattın aktif olup olmadığını, operatör bilgisini ve benzeri temel ağ durumlarını kontrol etmek için kullanılabilir. Bu izin; arama kayıtlarını, konuşma içeriklerini, SMS mesajlarını veya rehberinizi okumak için kullanılmaz."
            />

            <SectionWithList
              title="2.3 Konum İzinleri"
              items={[
                "android.permission.ACCESS_COARSE_LOCATION",
                "android.permission.ACCESS_FINE_LOCATION",
                "android.permission.ACCESS_BACKGROUND_LOCATION",
                "android.permission.FOREGROUND_SERVICE_LOCATION",
              ]}
              description="Konum izinleri, kullanılan cihazın genel konumunu tespit ederek makine yerleşimini yönetmek, bağlantı kalitesini izlemek veya isteğe bağlı konum-temelli raporlama fonksiyonlarını desteklemek için kullanılabilir. Uygulama, konum verilerini kullanıcı profili çıkarmak veya pazarlama amacıyla kullanmaz; konum verileri üçüncü taraflara satılmaz veya reklam amaçlı paylaşılmaz."
            />

            <SectionWithList
              title="2.4 Depolama İzinleri"
              items={[
                "android.permission.READ_EXTERNAL_STORAGE",
                "android.permission.WRITE_EXTERNAL_STORAGE",
              ]}
              description="Bu izinler; uygulamanın log dosyalarını, geçici yapılandırma dosyalarını veya makineyle ilgili teknik verileri cihaz depolamasına kaydedebilmesi ve okuyabilmesi için kullanılabilir. Uygulama, kullanıcıya ait kişisel fotoğraf, video, belge gibi içerikleri okumaz veya başka yere aktarmaya çalışmaz."
            />

            <SectionWithList
              title="2.5 NFC İzni"
              items={["android.permission.NFC"]}
              description="Bu izin, cihazın NFC özelliğini kullanarak kart/etiket okuma gibi fonksiyonları (örneğin kimlik doğrulama veya cihaz eşleştirme) desteklemek için alınmıştır. Okunan veriler yalnızca ilgili işlem için kullanılır ve üçüncü taraflarla paylaşılmaz."
            />

            <SectionWithList
              title="2.6 Ön Plan ve Arka Plan Servis İzinleri"
              items={[
                "android.permission.FOREGROUND_SERVICE",
                "android.permission.FOREGROUND_SERVICE_LOCATION",
              ]}
              description="Bu izinler, uygulamanın bazı işlemleri (bağlantı takibi, makine durumu izlemesi vb.) kullanıcı ekranı kapalıyken bile güvenli şekilde sürdürebilmesi için gereklidir. Söz konusu servisler yalnızca teknik görevler için kullanılır."
            />

            <SectionWithList
              title="2.7 Sistem Alert ve Titreşim İzinleri"
              items={[
                "android.permission.SYSTEM_ALERT_WINDOW",
                "android.permission.VIBRATE",
              ]}
              description="<code>SYSTEM_ALERT_WINDOW</code> izni, belirli sistem uyarıları veya kritik bilgilendirmelerin (örneğin makine hatası, bağlantı problemi) ekran üzerinde gösterilebilmesi için kullanılabilir. <code>VIBRATE</code> izni, kullanıcıya titreşimli uyarı vermek için kullanılır. Bu izinler herhangi bir kişisel veri okuma veya üçüncü taraflarla paylaşma amacı taşımaz."
            />

            <SectionWithList
              title="2.8 Konu Dışı Yapılandırmalar"
              items={["<queries> bloğu ile tanımlanan VIEW intentleri"]}
              description="Uygulama, bazı dış linkleri veya tarayıcıyı açmak için sistemde ilgili uygulama olup olmadığını kontrol edebilir. Bu kontroller, kullanıcı deneyimini iyileştirmek için yapılır ve profil oluşturma amacıyla kullanılmaz."
            />
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              3. Verilerin Paylaşımı
            </h2>
            <p className="leading-relaxed mb-3">
              Uygulama tarafından işlenen tüm teknik veriler, yalnızca sistemin
              çalışması, hata tespiti ve güvenliğin sağlanması amacıyla
              kullanılır.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                Veriler{" "}
                <strong>
                  hiçbir üçüncü tarafla pazarlama veya reklam amacıyla
                  paylaşılmamaktadır.
                </strong>
              </li>
              <li>
                Veriler, yasal zorunluluklar ve resmi makamların usulüne uygun
                talepleri dışında üçüncü kişilere aktarılmaz.
              </li>
              <li>
                Kişisel iletişim içerikleri (arama kayıtları, SMS içerikleri,
                rehber, kişisel dosyalar vb.) uygulama tarafından okunmaz veya
                işlenmez.
              </li>
            </ul>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              4. Veri Saklama Süresi
            </h2>
            <p className="leading-relaxed">
              Uygulama tarafından tutulan teknik kayıtlar ve log verileri,
              yalnızca gerekli olduğu sürece saklanır ve sonrasında silinir veya
              anonim hale getirilir. Saklama süreleri yasal yükümlülükler ve
              operasyonel ihtiyaçlar dikkate alınarak belirlenir.
            </p>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              5. Güvenlik
            </h2>
            <p className="leading-relaxed">
              Uygulama ve sunucu bileşenlerinde yetkisiz erişimi önlemek için
              uygun teknik ve idari güvenlik önlemleri uygulanmaktadır. Bununla
              birlikte, internet üzerinden yapılan hiçbir veri aktarımının %100
              güvenli olduğu garanti edilemez.
            </p>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              6. Kullanıcı Hakları
            </h2>
            <p className="leading-relaxed">
              Bulunduğunuz ülkenin veri koruma mevzuatına göre, verilerinize
              erişme, düzeltilmesini talep etme, silinmesini isteme veya
              işlenmesine itiraz etme gibi haklara sahip olabilirsiniz. Bu
              haklarınızla ilgili taleplerinizi aşağıdaki iletişim adresine
              iletebilirsiniz.
            </p>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              7. İletişim
            </h2>
            <p className="leading-relaxed mb-2">
              Bu gizlilik politikası veya veri işleme faaliyetlerimizle ilgili
              sorularınız için bizimle aşağıdaki bilgiler üzerinden iletişime
              geçebilirsiniz:
            </p>
            <p className="leading-relaxed">
              E-posta:{" "}
              <a
                href="mailto:info@bayiner.com"
                className="text-blue-600 underline"
              >
                info@bayiner.com
              </a>
              <br />
              Şirket: Bayiner Elektronik San. ve Tic. A.Ş.
            </p>
          </article>

          <article>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              8. Değişiklikler
            </h2>
            <p className="leading-relaxed">
              Bu gizlilik politikası zaman zaman güncellenebilir. Güncel sürüm
              her zaman bu sayfada yayınlanır ve yayınlandığı tarihten itibaren
              geçerli olur.
            </p>
          </article>
        </section>
      </div>
    </div>
  );
};

const SectionWithList = ({ title, items, description }) => (
  <div className="mb-6">
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-2">
      {items.map((item, index) => (
        <li key={index}>
          <code>{item}</code>
        </li>
      ))}
    </ul>
    <p
      className="leading-relaxed"
      dangerouslySetInnerHTML={{ __html: description }}
    />
  </div>
);

export default PrivacyPolicyPage;
